import { useCallback, useEffect, useState } from 'react'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import Alert from '@mui/joy/Alert'
import IconButton from '@mui/joy/IconButton'
import Tooltip from '@mui/joy/Tooltip'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import ModalClose from '@mui/joy/ModalClose'
import DialogTitle from '@mui/joy/DialogTitle'
import DialogContent from '@mui/joy/DialogContent'
import DialogActions from '@mui/joy/DialogActions'
import CircularProgress from '@mui/joy/CircularProgress'
import RefreshIcon from '@mui/icons-material/Refresh'
import SyncIcon from '@mui/icons-material/Sync'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import GoogleIcon from '@mui/icons-material/Google'
import WindowIcon from '@mui/icons-material/Window'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { adminCalendars, syncCalendar, disconnectCalendar, calendarConnectUrl, type DsGmeetRepCalendar, type CalendarPlatform } from '../../api/dsGmeetApi'
import { useCurrentUserId } from '../../hooks/useCurrentUserId'

const statusColor: Record<DsGmeetRepCalendar['status'], 'success' | 'danger' | 'neutral'> = {
    CONNECTED: 'success',
    REVOKED: 'danger',
    DISCONNECTED: 'neutral',
}

export function CalendarsTab() {
    const currentUserId = useCurrentUserId()
    const [items, setItems] = useState<DsGmeetRepCalendar[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [syncingId, setSyncingId] = useState<string | null>(null)
    const [confirmDisconnect, setConfirmDisconnect] = useState<DsGmeetRepCalendar | null>(null)
    const [disconnecting, setDisconnecting] = useState(false)
    const [showAdd, setShowAdd] = useState(false)
    const [addError, setAddError] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            setItems(await adminCalendars())
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    const handleDisconnectConfirm = async () => {
        if (!confirmDisconnect) return
        setDisconnecting(true)
        try {
            await disconnectCalendar(confirmDisconnect.recall_calendar_id)
            setConfirmDisconnect(null)
            await load()
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err))
        } finally {
            setDisconnecting(false)
        }
    }

    const startConnect = (platform: CalendarPlatform) => {
        const userId = currentUserId
        if (!userId) {
            setAddError('Not signed in')
            return
        }
        setAddError('')
        const url = calendarConnectUrl(platform, userId)
        const popup = window.open(url, 'calendar_oauth', 'popup,width=600,height=700,left=200,top=100')
        if (!popup) {
            setAddError('Popup blocked — allow popups and retry.')
            return
        }
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'CALENDAR_AUTH_SUCCESS') {
                window.removeEventListener('message', handler)
                popup.close()
                setShowAdd(false)
                void load()
            } else if (e.data?.type === 'CALENDAR_AUTH_ERROR') {
                window.removeEventListener('message', handler)
                popup.close()
                setAddError(e.data.message ?? 'Connection failed.')
            }
        }
        window.addEventListener('message', handler)
    }

    return (
        <>
            <Stack gap={0.75}>
                {/* Single-line: label + chips + actions */}
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    {/* Label */}
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ flexShrink: 0 }}>
                        <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography level="title-sm">Connected calendars</Typography>
                        {loading && <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px' }} />}
                    </Stack>

                    {/* Calendar chips */}
                    {items.map((r) => (
                        <Sheet
                            key={r.recall_calendar_id}
                            variant="outlined"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                pl: 1,
                                pr: 0.25,
                                py: 0.25,
                                borderRadius: 'xl',
                                bgcolor: 'background.surface',
                            }}
                        >
                            <Typography level="body-xs" sx={{ fontWeight: 500 }}>
                                {r.account_email}
                            </Typography>
                            <Chip size="sm" variant="soft" color={statusColor[r.status]} sx={{ minHeight: 18, fontSize: 10, px: 0.5, borderRadius: 'xl' }}>
                                {r.status}
                            </Chip>
                            <Tooltip title="Sync events">
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    disabled={r.status !== 'CONNECTED' || syncingId !== null}
                                    loading={syncingId === r.recall_calendar_id}
                                    onClick={async () => {
                                        setSyncingId(r.recall_calendar_id)
                                        try {
                                            await syncCalendar(r.recall_calendar_id)
                                        } catch {
                                            /* silent */
                                        } finally {
                                            setSyncingId(null)
                                            void load()
                                        }
                                    }}
                                    sx={{ minHeight: 22, minWidth: 22, borderRadius: 'xl' }}
                                >
                                    <SyncIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Disconnect">
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color="danger"
                                    onClick={() => setConfirmDisconnect(r)}
                                    sx={{ minHeight: 22, minWidth: 22, borderRadius: 'xl' }}
                                >
                                    <CloseIcon sx={{ fontSize: 12 }} />
                                </IconButton>
                            </Tooltip>
                        </Sheet>
                    ))}

                    {/* Inline add form — just provider buttons, no user ID needed */}
                    {showAdd ? (
                        <Sheet
                            variant="outlined"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                pl: 0.75,
                                pr: 0.25,
                                py: 0.25,
                                borderRadius: 'xl',
                                borderStyle: 'dashed',
                            }}
                        >
                            <Tooltip title="Connect Google Calendar">
                                <Button
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    startDecorator={<GoogleIcon sx={{ fontSize: 13 }} />}
                                    onClick={() => startConnect('google_calendar')}
                                    sx={{ minHeight: 24, fontSize: 11, px: 0.75, borderRadius: 'xl' }}
                                >
                                    Google
                                </Button>
                            </Tooltip>
                            <Tooltip title="Connect Microsoft Outlook">
                                <Button
                                    size="sm"
                                    variant="plain"
                                    color="neutral"
                                    startDecorator={<WindowIcon sx={{ fontSize: 13 }} />}
                                    onClick={() => startConnect('microsoft_outlook')}
                                    sx={{ minHeight: 24, fontSize: 11, px: 0.75, borderRadius: 'xl' }}
                                >
                                    Outlook
                                </Button>
                            </Tooltip>
                            <IconButton
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={() => {
                                    setShowAdd(false)
                                    setAddError('')
                                }}
                                sx={{ minHeight: 22, minWidth: 22, borderRadius: 'xl' }} /* cancel */
                            >
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Sheet>
                    ) : (
                        <Tooltip title="Connect a rep calendar">
                            <IconButton
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                onClick={() => setShowAdd(true)}
                                sx={{ minHeight: 26, minWidth: 26, borderRadius: 'xl' }}
                            >
                                <AddIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* Refresh icon at the end */}
                    <Tooltip title="Refresh">
                        <IconButton
                            size="sm"
                            variant="plain"
                            color="neutral"
                            onClick={load}
                            disabled={loading}
                            sx={{ minHeight: 26, minWidth: 26, borderRadius: 'xl' }}
                        >
                            <RefreshIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {addError && (
                    <Typography level="body-xs" color="danger" sx={{ pl: '26px' }}>
                        {addError}
                    </Typography>
                )}
                {error && (
                    <Alert color="danger" size="sm">
                        {error}
                    </Alert>
                )}
            </Stack>

            {/* Disconnect confirmation modal */}
            <Modal open={!!confirmDisconnect} onClose={() => !disconnecting && setConfirmDisconnect(null)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 400 }}>
                    <ModalClose disabled={disconnecting} />
                    <DialogTitle>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <WarningAmberIcon color="warning" />
                            Disconnect calendar?
                        </Stack>
                    </DialogTitle>
                    <DialogContent>
                        <Typography level="body-sm">
                            <strong>{confirmDisconnect?.account_email}</strong> will stop being auto-recorded. Future qualifying meetings on this calendar will
                            be skipped.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="solid" color="danger" loading={disconnecting} onClick={() => void handleDisconnectConfirm()}>
                            Disconnect
                        </Button>
                        <Button variant="plain" color="neutral" disabled={disconnecting} onClick={() => setConfirmDisconnect(null)}>
                            Cancel
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </>
    )
}
